import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { rupiah, tanggal } from "@/lib/format";
import { PublicPage } from "@/components/landing/PublicPage";
import { FinanceSection } from "@/components/landing/FinanceSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Keuangan() {
  const [finance, setFinance] = useState(null);
  const [txs, setTxs] = useState([]);
  const [type, setType] = useState("all");
  const [month, setMonth] = useState("all");

  useEffect(() => {
    api.get("/finance/summary").then((r) => setFinance(r.data)).catch(() => {});
    api.get("/transactions?limit=2000").then((r) => setTxs(r.data)).catch(() => {});
  }, []);

  const months = useMemo(() => [...new Set(txs.map((t) => t.date?.slice(0, 7)).filter(Boolean))].sort().reverse(), [txs]);
  const shown = txs.filter((t) => (type === "all" || t.type === type) && (month === "all" || t.date?.startsWith(month)));
  const sum = (k) => shown.filter((t) => t.type === k).reduce((s, t) => s + t.amount, 0);

  return (
    <PublicPage testId="keuangan-page" eyebrow="Transparansi Keuangan" title="Laporan Keuangan Masjid" desc="Setiap rupiah infaq jamaah dicatat dan dilaporkan secara terbuka. Berikut ringkasan kas, grafik arus kas, dan riwayat transaksi lengkap.">
      {(profile) => (
        <div className="space-y-14">
          <FinanceSection finance={finance} transactions={txs} profile={profile} full />
          <section data-testid="keuangan-riwayat">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-emerald-950">Riwayat Transaksi</h2>
              <div className="flex flex-wrap gap-3 ml-auto">
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="w-40 rounded-full bg-white" data-testid="keuangan-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Semua Jenis</SelectItem><SelectItem value="masuk">Pemasukan</SelectItem><SelectItem value="keluar">Pengeluaran</SelectItem></SelectContent>
                </Select>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger className="w-44 rounded-full bg-white" data-testid="keuangan-filter-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Bulan</SelectItem>
                    {months.map((m) => <SelectItem key={m} value={m}>{new Date(`${m}-01`).toLocaleDateString("id-ID", { month: "long", year: "numeric" })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600" data-testid="keuangan-filter-summary">
              <span>{shown.length} transaksi</span>
              <span>Masuk: <b className="text-emerald-800">{rupiah(sum("masuk"))}</b></span>
              <span>Keluar: <b className="text-red-700">{rupiah(sum("keluar"))}</b></span>
            </div>
            <div className="mt-4 rounded-3xl bg-white border border-emerald-900/10 overflow-x-auto">
              {shown.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-500" data-testid="keuangan-riwayat-empty">Tidak ada transaksi.</p>
              ) : (
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-emerald-50 text-emerald-900 text-left"><tr><th className="px-5 py-3">Tanggal</th><th className="px-5 py-3">Kategori</th><th className="px-5 py-3">Keterangan</th><th className="px-5 py-3 text-right">Nominal</th></tr></thead>
                  <tbody>
                    {shown.map((t) => (
                      <tr key={t.id} className="border-t border-emerald-900/5" data-testid={`keuangan-row-${t.id}`}>
                        <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{tanggal(t.date)}</td>
                        <td className="px-5 py-3 font-medium text-emerald-950">{t.category}</td>
                        <td className="px-5 py-3 text-slate-600">{t.description || "-"}</td>
                        <td className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${t.type === "masuk" ? "text-emerald-800" : "text-red-700"}`}>{t.type === "masuk" ? "+" : "-"}{rupiah(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      )}
    </PublicPage>
  );
}
